import { CommonModule, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatStepperModule } from '@angular/material/stepper';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';
import {
  CheckboxFieldComponent,
  DatepickerFieldComponent,
  InputFieldComponent,
  Item,
  ItemsListComponent,
  RadioFieldComponent,
  RegioniProvinceComuniComponent,
  SelectFieldComponent,
  SelectTipologicaFieldComponent,
  SwitchFieldComponent,
  TextareaFieldComponent,
} from '../../form';
import { MoniserFieldComponent } from '../../form/moniser-field/moniser-field.component';
import {
  UploadedFile,
  UploadFileComponent,
} from '../../form/upload-file/upload-file.component';
import {
  CustomModalOptions,
  ModalType,
  SmartModalAction,
  SmartModalService,
} from '../../smart-modal';
import { SnackbarService } from '../../snackbar';
import { DynamicScaleComponent } from '../dynamic-form-builder/dynamic-scale/dynamic-scale.component';
import { DynamicFormHttpService } from '../http/dynamicform-http.service';
import { ComputedField, FieldDefinition, ScaleSection } from '../models';
import { DynamicFormDTO } from '../models/dynamic-form-dto.model';
import {
  FieldTypeENUM,
  IDynamicForm,
  IFields,
} from '../models/dynamic-form.model';
import { FormMode } from '../models/form-mode.enum';
import { DynamicFormViewerService } from '../services/viewer.service';

export type FieldValue =
  | string
  | number
  | boolean
  | null
  | Date
  | string[]
  | object;

export interface ProcessedField {
  description: string;
  value: FieldValue;
  type: string;
  id: string;
  fields?: ProcessedField[];
}

export interface ProcessedSection {
  title: string;
  description: string;
  fields: ProcessedField[];
  id: string;
}

export interface ProcessedForm {
  title: string;
  description: string;
  sections: ProcessedSection[];
  id: string;
}

@UntilDestroy()
@Component({
  selector: 'siat-dynamic-form-viewer',
  imports: [
    CommonModule,
    NgTemplateOutlet,
    DynamicScaleComponent,
    UpperCasePipe,
    ReactiveFormsModule,
    MatStepperModule,
    MatExpansionModule,
    MatButtonModule,
    InputFieldComponent,
    TextareaFieldComponent,
    SelectFieldComponent,
    DatepickerFieldComponent,
    UploadFileComponent,
    MatCard,
    MatCardContent,
    CheckboxFieldComponent,
    RadioFieldComponent,
    SwitchFieldComponent,
    RegioniProvinceComuniComponent,
    SelectTipologicaFieldComponent,
    ItemsListComponent,
    MoniserFieldComponent,
  ],
  templateUrl: './dynamic-form-viewer.component.html',
  styles: [
    `
      .section-group {
        background-color: transparent;
        border-radius: 1rem;
        border: 1px solid #d6c2c4;
        padding: 1rem 1rem 0.5rem;
      }
    `,
  ],
})
export class DynamicFormViewerComponent {
  /**
   * The injected services.
   */
  private readonly dynamicFormViewerService = inject(DynamicFormViewerService);
  private readonly dynamicFormHttpService = inject(DynamicFormHttpService);
  private readonly snackBarService = inject(SnackbarService);
  private readonly smartModalService = inject(SmartModalService);

  /**
   * The dynamic form data transfer object containing the JSON configuration and metadata.
   */
  readonly dynamicFormDto = input<DynamicFormDTO>(null);

  /**
   * The form mode.
   */
  readonly formMode = input<FormMode>();

  /**
   * Flag to enable read-only mode.
   */
  readonly readModeEnabled = input<boolean>(false);

  /**
   * Flag to enable drag-and-drop field reordering.
   */
  readonly hideTitle = input<boolean>(false);

  /**
   * When true, the form is disabled immediately after being built (e.g. for read-only collaboratori).
   */
  readonly disableForm = input<boolean>(false);

  /**
   * The last processed JSON string to prevent unnecessary rebuilds
   * of the form when the same JSON is emitted multiple times.
   */
  private lastProcessedJson: string | null = null;

  /**
   * The form instance.
   */
  dynamicForm: FormGroup;

  /**
   * Reference for field type enum.
   */
  readonly fieldTypeEnum = FieldTypeENUM;

  /**
   * Default value for priority field options.
   */
  readonly priorityOptions: Item[] = [
    { id: 1, name: 'Alta' },
    { id: 2, name: 'Media' },
    { id: 3, name: 'Bassa' },
  ];

  /**
   * Output for hidden save event.
   */
  loadHiddenSave = output();

  /** * Computes the form data for Read Mode.
   * It filters out hidden fields dynamically.
   */
  readonly fieldValuesToRead = computed<ProcessedForm | null>(() => {
    const dto = this.dynamicFormDto();
    if (dto && this.readModeEnabled()) {
      return this.transformDynamicJson(dto);
    }
    return null;
  });

  constructor() {
    effect(() => {
      const dto = this.dynamicFormDto();
      if (!dto) return;

      if (!dto.dynamicJson) {
        console.warn(
          `DynamicFormViewer: "${dto.title ?? dto.id ?? 'form'}" non ha uno schema (dynamicJson vuoto). Verrà mostrato un form vuoto per non bloccare l'avanzamento.`,
        );
      }
      const dynamicJson = dto.dynamicJson || '{}';

      if (dynamicJson === this.lastProcessedJson) {
        return;
      }

      this.lastProcessedJson = dynamicJson;
      this.buildForm({ ...dto, dynamicJson });
    });

    effect(() => {
      const form = this.dynamicFormViewerService.formGroup();
      if (!form) return;
      if (this.disableForm()) {
        form.disable({ emitEvent: false });
      } else {
        form.enable({ emitEvent: false });
      }
    });
  }

  /**
   * Parses the JSON and instructs the service to build the form
   * and setup conditional logic listeners.
   */
  private buildForm(dynamicForm: DynamicFormDTO): void {
    try {
      const newFields = JSON.parse(dynamicForm?.dynamicJson) as IDynamicForm;
      this.dynamicForm =
        this.dynamicFormViewerService.buildViewerForm(newFields);
      this.dynamicFormViewerService.setupConditionalLogic(this.dynamicForm);
    } catch (error) {
      console.error('Error parsing JSON:', error);
    }
  }

  /**
   * Checks if a field should be visible based on conditional logic.
   * Called dynamically from the template.
   * @returns {boolean} True if visible.
   */
  isFieldVisible(fieldFormGroup: FormGroup): boolean {
    const hasCond = fieldFormGroup.get('hasCondition')?.value;

    if (!fieldFormGroup || !hasCond) return true;

    const condId = fieldFormGroup.get('conditionFieldId')?.value;
    const condVal = fieldFormGroup.get('conditionValue')?.value;
    const parentGroup = this.findParentGroupById(condId);

    return this.dynamicFormViewerService.compareCondition(parentGroup, condVal);
  }

  /**
   * Deep search to find a field's FormGroup across all form sections.
   */
  private findParentGroupById(id: string): FormGroup | null {
    if (!this.dynamicForm) return null;
    const sections = this.dynamicForm.get('sections') as FormArray;

    if (!sections) return null;

    for (const section of sections.controls) {
      const fields = section.get('fields') as FormArray;
      const field = this.findFieldById(fields, id);
      if (field) return field;
    }
    return null;
  }

  /**
   * Recursively searches fields, including accordion children.
   */
  private findFieldById(
    fields: FormArray | null,
    id: string,
  ): FormGroup | null {
    if (!fields) return null;

    for (const field of fields.controls) {
      const fieldGroup = field as FormGroup;
      if (fieldGroup.get('id')?.value === id) return fieldGroup;

      const nestedField = this.findFieldById(
        fieldGroup.get('fields') as FormArray,
        id,
      );
      if (nestedField) return nestedField;
    }

    return null;
  }

  /**
   * @returns { FormArray } Helper to get the sections FormArray from the main form.
   */
  getSectionsArray(): FormArray {
    return this.dynamicForm?.get('sections') as FormArray;
  }

  /**
   * @returns { FormGroup } Helper to get the fields FormGroup of a specific section.
   */
  getSectionFieldsGroup(sectionIndex: number): FormGroup {
    return this.getSectionsArray().at(sectionIndex).get('fields') as FormGroup;
  }

  /**
   * @returns { FormGroup } Helper to get a specific field FormGroup based on section and field indices.
   */
  getFieldGroup(sectionIndex: number, fieldIndex: number): FormGroup {
    const sectionGroup = this.getSectionsArray().at(sectionIndex) as FormGroup;
    const fieldsArray = sectionGroup.get('fields') as FormArray;

    return fieldsArray.at(fieldIndex) as FormGroup;
  }

  /**
   * @returns { FormGroup } Helper to get a specific field FormGroup from a FormArray.
   */
  getFieldGroupFromArray(
    fieldsArray: FormArray,
    fieldIndex: number,
  ): FormGroup {
    return fieldsArray.at(fieldIndex) as FormGroup;
  }

  /**
   * @returns { FormArray } Helper to get nested accordion fields.
   */
  getAccordionFieldsArray(fieldFormGroup: FormGroup): FormArray {
    return fieldFormGroup.get('fields') as FormArray;
  }

  /**
   * @returns { FormGroup } Helper to get the options FormArray of a specific field, if it has options.
   */
  getOptionsGroup(fg: FormGroup, optionIndex: number): FormGroup {
    const optionsFormArray = fg.get('options') as FormArray;

    return optionsFormArray.at(optionIndex) as FormGroup;
  }

  /**
   * @returns { FormGroup } Helper to get the FormGroup for region/province/comune.
   */
  regProvComuneValueFg(fg: FormGroup): FormGroup {
    return fg.get('value') as FormGroup;
  }

  /**
   * @returns {boolean} Helper to determine if a field is of a scale type.
   */
  isScaleField(type: string): boolean {
    return this.dynamicFormViewerService.isScaleField(type);
  }

  /**
   * @returns { UploadedFile | null } Helper to cast a field value to UploadedFile for file preview components.
   */
  getFileValues(files: FieldValue): UploadedFile | null {
    return files as UploadedFile;
  }

  /**
   * Resolves the base64 preview string for an uploaded file.
   */
  resolvePreview = (
    fileId: string,
  ): Observable<string | ArrayBuffer | null> => {
    return this.dynamicFormHttpService
      .fetchGetFileById(fileId)
      .pipe(
        map((response) =>
          response?.file && response?.type
            ? `data:${response.type};base64,${response.file}`
            : null,
        ),
      );
  };

  /**
   * Triggers the deletion flow for an uploaded file with a confirmation modal.
   */
  onDeleteFile(uploadedFile: UploadedFile, formControl: AbstractControl): void {
    const control = formControl as FormControl;
    const modalConfig = {
      title: 'Conferma eliminazione',
      message: `Sei sicuro di voler eliminare il file ${uploadedFile.nomeFile}?`,
    } as CustomModalOptions;

    this.smartModalService
      .openSmartModal(ModalType.CONFIRM, modalConfig)
      .pipe(
        take(1),
        filter((action) => action === SmartModalAction.CONFIRMED),
        switchMap(() =>
          this.dynamicFormHttpService
            .fetchDeleteFile(uploadedFile.fileId, uploadedFile?.fromMoniser)
            .pipe(
              tap(() => {
                control.patchValue(null);
                this.loadHiddenSave.emit();
                this.snackBarService.showSuccessSnackbar('File eliminato');
              }),
            ),
        ),

        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Transforms raw JSON into a processed format suitable for Read-Only mode.
   */
  private transformDynamicJson(
    dynamicFormDto: DynamicFormDTO,
  ): ProcessedForm | null {
    if (!dynamicFormDto.dynamicJson) return null;

    const dynamicData: IDynamicForm = JSON.parse(dynamicFormDto.dynamicJson);
    const rawDataMap = new Map<string, IFields>();

    dynamicData.sections.forEach((s) => {
      this.collectRawFields(s.fields || [], rawDataMap);
    });

    return {
      id: dynamicData.id,
      title: dynamicData.title,
      description: dynamicData.description,
      sections: dynamicData.sections.map((section) => {
        // Controlla se in questa sezione c'è almeno un campo di tipo scala
        const hasScaleField = section.fields?.some((field) =>
          this.dynamicFormViewerService.isScaleField(field.type),
        );

        return {
          id: section.id,
          title: section.title,
          // Se c'è una scala, azzeriamo la descrizione.
          // L' @if (section?.description) del template farà il resto nascondendola.
          description: hasScaleField ? '' : section.description,
          fields: this.processFieldsRecursively(section.fields, rawDataMap),
        };
      }),
    };
  }

  /**
   * Processes fields for read-only view, filtering out those hidden by conditional logic.
   */
  private processFieldsRecursively(
    fields: IFields[],
    rawDataMap: Map<string, IFields>,
  ): ProcessedField[] {
    return fields
      .filter((field) => this.isFieldConditionMet(field, rawDataMap))
      .flatMap((field) => this.processSingleReadModeField(field, rawDataMap));
  }

  /**
   * Checks if a field's condition is met based on the current values of its parent fields.
   */
  private isFieldConditionMet(
    field: IFields,
    rawDataMap: Map<string, IFields>,
  ): boolean {
    if (!field.hasCondition || !field.conditionFieldId) return true;

    const parentRawData = rawDataMap.get(field.conditionFieldId);
    if (!parentRawData) return false;

    const fakeParentGroup = new FormGroup({
      id: new FormControl(parentRawData.id),
      type: new FormControl(parentRawData.type),
      value: new FormControl(parentRawData.value),
      options: new FormArray(
        (parentRawData.options || []).map(
          (opt) =>
            new FormGroup({
              id: new FormControl(opt.id),
              selected: new FormControl(opt.selected),
            }),
        ),
      ),
    });

    return this.dynamicFormViewerService.compareCondition(
      fakeParentGroup,
      field.conditionValue,
    );
  }

  /**
   * Processes a single field for read-only display. If it's a scale type, it flattens it into multiple entries.
   */
  private processSingleReadModeField(
    field: IFields,
    rawDataMap: Map<string, IFields>,
  ): ProcessedField[] {
    const isScaleType = this.dynamicFormViewerService.isScaleField(field.type);

    if (field.type === FieldTypeENUM.ACCORDION) {
      return [this.extractAccordionField(field, rawDataMap)];
    }

    if (isScaleType && field.config) {
      return this.extractScaleFields(field);
    }

    return [this.extractNormalField(field)];
  }

  /**
   * Flattens and processes a scale field into multiple ProcessedField entries for read-only display.
   */
  private extractScaleFields(field: IFields): ProcessedField[] {
    const scaleValues = field.value || {};
    const scaleProcessedFields: ProcessedField[] = [];

    field.config?.sections?.forEach((scaleSection: ScaleSection) => {
      scaleSection.fields?.forEach((scaleField: FieldDefinition) => {
        const rawValue = scaleValues[scaleField.key];
        let finalValue = rawValue ? String(rawValue) : 'N/A';

        if (
          scaleField.valueDescriptions &&
          rawValue &&
          scaleField.valueDescriptions[String(rawValue)]
        ) {
          finalValue = `${finalValue} - ${scaleField.valueDescriptions[String(rawValue)]}`;
        }

        scaleProcessedFields.push({
          description: scaleField.label || scaleField.description || '',
          value: finalValue,
          type: scaleField.type || 'number',
          id: `${scaleField.key}_${field.id}`,
        });
      });
    });

    field.config?.computedFields?.forEach((computedField: ComputedField) => {
      const rawValue = scaleValues[computedField.key];
      const finalValue =
        rawValue !== undefined && rawValue !== null ? String(rawValue) : 'N/A';

      scaleProcessedFields.push({
        description: computedField.label || '',
        value: finalValue,
        type: computedField.type || 'text',
        id: `${computedField.key}_${field.id}`,
      });
    });

    return scaleProcessedFields;
  }

  /**
   * Extracts a normal field's value and description for read-only display.
   */
  private extractNormalField(field: IFields): ProcessedField {
    return {
      description: field.label || '',
      value: this.extractFieldValueForReadMode(field),
      type: field.type,
      id: field.id,
    };
  }

  /**
   * Keeps accordion fields grouped for read-only display.
   */
  private extractAccordionField(
    field: IFields,
    rawDataMap: Map<string, IFields>,
  ): ProcessedField {
    return {
      description: field.label || '',
      value: field.placeholder || '',
      type: field.type,
      id: field.id,
      fields: this.processFieldsRecursively(field.fields || [], rawDataMap),
    };
  }

  /**
   * Builds a field lookup recursively for conditional logic in read mode.
   */
  private collectRawFields(
    fields: IFields[],
    rawDataMap: Map<string, IFields>,
  ): void {
    fields.forEach((field) => {
      if (field.id) rawDataMap.set(field.id, field);
      if (field.type === FieldTypeENUM.ACCORDION) {
        this.collectRawFields(field.fields || [], rawDataMap);
      }
    });
  }

  /**
   * Formats the raw field value into a readable string/array for the UI.
   */
  private extractFieldValueForReadMode(field: IFields): FieldValue {
    if (field.options && field.options.length > 0) {
      return field.options
        .filter((o) => o.selected)
        .map((o) => o.name)
        .join(', ');
    }
    if (field.value === true) return 'Sì';
    if (field.value === false) return 'No';
    return field.value;
  }
}
